/* eslint-disable react-refresh/only-export-components */
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { api } from "../api";
import type { Account, BootstrapData, Category } from "../types";

interface AppContextValue {
  accounts: Account[];
  categories: Category[];
  userEmail: string;
  quickAddOpen: boolean;
  openQuickAdd: () => void;
  closeQuickAdd: () => void;
  refreshBootstrap: () => Promise<void>;
  toast: string;
  dataVersion: number;
  notify: (message: string) => void;
}

const AppContext = createContext<AppContextValue | null>(null);

export function AppProvider({ initial, children }: { initial: BootstrapData; children: ReactNode }) {
  const [accounts, setAccounts] = useState(initial.accounts);
  const [categories, setCategories] = useState(initial.categories);
  const [quickAddOpen, setQuickAddOpen] = useState(false);
  const [toast, setToast] = useState("");
  const [dataVersion, setDataVersion] = useState(0);

  const refreshBootstrap = useCallback(async () => {
    const data = await api.bootstrap();
    setAccounts(data.accounts);
    setCategories(data.categories);
  }, []);

  const notify = useCallback((message: string) => setToast(message), []);
  useEffect(() => {
    let active = true;
    let refreshing = false;
    async function refreshOnReturn() {
      if (document.visibilityState !== "visible" || refreshing) return;
      refreshing = true;
      try { await refreshBootstrap(); if (active) setDataVersion((value) => value + 1); }
      catch { /* Keep the current view when returning without a connection. */ }
      finally { refreshing = false; }
    }
    document.addEventListener("visibilitychange", refreshOnReturn);
    window.addEventListener("focus", refreshOnReturn);
    return () => { active = false; document.removeEventListener("visibilitychange", refreshOnReturn); window.removeEventListener("focus", refreshOnReturn); };
  }, [refreshBootstrap]);
  useEffect(() => {
    if (!toast) return;
    const timer = window.setTimeout(() => setToast(""), 2800);
    return () => window.clearTimeout(timer);
  }, [toast]);

  const value = useMemo(() => ({ accounts, categories, userEmail: initial.user?.email ?? "", quickAddOpen, openQuickAdd: () => setQuickAddOpen(true), closeQuickAdd: () => setQuickAddOpen(false), refreshBootstrap, toast, dataVersion, notify }), [accounts, categories, initial.user?.email, notify, quickAddOpen, refreshBootstrap, toast, dataVersion]);
  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

export function useApp() {
  const value = useContext(AppContext);
  if (!value) throw new Error("useApp must be used inside AppProvider");
  return value;
}
